// EventDetailPage — T2 (Object Detail + Tabs) layout.
// Displays a single event with tabbed sections.
// Route: /t/:slug/events/:eventId (and sub-routes for tabs)

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  eventsApi,
  eventLifecycleStateLabels,
  eventLifecycleStateColors,
} from '@/lib/api/events'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { DetailPageShell, type TabDef } from '@/components/shared/DetailPageShell'
import { Button } from '@/components/ui/button'
import { MoveEventDialog } from './MoveEventDialog'
import { User, Calendar, FolderOpen, Tag, MoveRight } from 'lucide-react'

const tabs: TabDef[] = [
  { id: 'detail', label: 'Detail', path: '' },
  { id: 'risks', label: 'Risks', path: '/risks', countKey: 'linked_risk_count' },
  { id: 'actions', label: 'Actions', path: '/actions', countKey: 'linked_action_count' },
  { id: 'evidence', label: 'Evidence', path: '/evidence' },
  { id: 'history', label: 'Audit Log', path: '/history' },
]

export function EventDetailPage() {
  const { slug } = useTenant()
  const { eventId } = useParams({ strict: false }) as { eventId: string }
  const api = useApi()
  const evtApi = eventsApi(api)
  const [moveOpen, setMoveOpen] = useState(false)

  const {
    data: event,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.events.detail(slug, eventId),
    queryFn: () => evtApi.get(eventId),
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load event"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!event) return null

  return (
    <DetailPageShell
      title={event.title}
      code={event.short_code || undefined}
      stateLabel={eventLifecycleStateLabels[event.lifecycle_state] ?? event.lifecycle_state}
      stateColorClass={eventLifecycleStateColors[event.lifecycle_state]}
      backUrl={`/t/${slug}/events`}
      backLabel="Back to event list"
      tabs={tabs}
      basePath={`/t/${slug}/events/${eventId}`}
      data={event as unknown as Record<string, unknown>}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => setMoveOpen(true)}>
            <MoveRight className="mr-1.5 h-3.5 w-3.5" />
            Move to...
          </Button>
          <MoveEventDialog
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
            eventId={eventId}
            currentCategoryId={event.category_id}
            currentCategoryName={event.category_name}
            onMoved={() => setMoveOpen(false)}
          />
        </>
      }
      metadata={
        <>
          <span className="flex items-center gap-1">
            <Tag className="h-3.5 w-3.5" />
            {event.event_type_name}
          </span>
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3.5 w-3.5" />
            {event.category_name}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {event.recorded_by_name}
          </span>
          {event.event_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(event.event_date + 'T00:00:00').toLocaleDateString()}
            </span>
          )}
          <span className="text-xs text-muted-foreground/60">
            Recorded {new Date(event.recorded_at).toLocaleDateString()}
          </span>
        </>
      }
    />
  )
}
